import { Fragment, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { getStorageUrl } from "@/lib/storage";
import Footer from "@/components/layout/Footer";

import LandingHeader from "@/components/landing/LandingHeader";
import LandingHero from "@/components/landing/LandingHero";
import FeaturedPrograms, {
  type FeaturedCurriculum,
} from "@/components/landing/FeaturedPrograms";
import HowItWorks from "@/components/landing/HowItWorks";
import AudienceSplit from "@/components/landing/AudienceSplit";
import PhotoGallery from "@/components/landing/PhotoGallery";
import ClosingCTA from "@/components/landing/ClosingCTA";
import CustomBlock, { type ContentBlock } from "@/components/landing/CustomBlock";
import { useGalleryImages } from "@/components/landing/useGalleryImages";

/** Number of program cards the design shows. Which three is admin-controlled. */
const FEATURED_PROGRAM_COUNT = 3;

/**
 * Marketing landing page — design direction "Signal".
 *
 * Section order still comes from homepage_content_blocks, so admins keep
 * control of sequence and which sections are live; this page maps each block
 * type onto its redesigned section rather than owning a fixed order.
 */
export default function Index() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  // Admin-set hero banner image.
  const { data: heroBannerSetting } = useQuery({
    queryKey: ["site_setting_hero_banner_public"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "hero_banner_image")
        .single();
      if (error && error.code !== "PGRST116") throw error;
      return data?.value;
    },
  });

  // The three featured programs. Ordered by sequence_order, which admins drag
  // to reorder in the admin Curriculum Reorder panel — that ordering is what
  // decides which three appear here.
  const { data: featuredCurricula, isLoading: isFeaturedCurriculaLoading } = useQuery({
    queryKey: ["featured-curricula-landing", FEATURED_PROGRAM_COUNT],
    queryFn: async (): Promise<FeaturedCurriculum[]> => {
      const { data: curricula, error } = await supabase
        .from("curricula")
        .select("id, title, description, thumbnail_url")
        .eq("is_published", true)
        .not("thumbnail_url", "is", null)
        .order("sequence_order", { ascending: true })
        .limit(FEATURED_PROGRAM_COUNT);
      if (error) throw error;

      // Audiences drive the eligibility tag pill on each card.
      const { data: audienceData } = await supabase
        .from("curriculum_audiences")
        .select("curriculum_id, audience_role");

      const audienceMap = new Map<string, string[]>();
      (audienceData || []).forEach((a) => {
        const existing = audienceMap.get(a.curriculum_id) || [];
        audienceMap.set(a.curriculum_id, [...existing, a.audience_role]);
      });

      return (curricula || []).map((c) => ({
        ...c,
        audiences: audienceMap.get(c.id) || [],
      }));
    },
  });

  // Homepage content blocks — the source of section order.
  const { data: contentBlocks } = useQuery({
    queryKey: ["homepage-content-blocks-public"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("homepage_content_blocks")
        .select("*")
        .eq("is_active", true)
        .order("sequence_order", { ascending: true });
      if (error) throw error;
      return data as ContentBlock[];
    },
  });

  const { images: galleryImages } = useGalleryImages();

  useEffect(() => {
    if (!loading && user) {
      navigate("/dashboard");
    }
  }, [user, loading, navigate]);

  // Hero photo: admin banner first, then the lead gallery image, then the first
  // program thumbnail as a last resort.
  const heroImageUrl =
    heroBannerSetting ||
    galleryImages[0]?.resolvedUrl ||
    getStorageUrl("course-content", featuredCurricula?.[0]?.thumbnail_url) ||
    null;

  return (
    <div className="min-h-screen overflow-x-hidden bg-eab-surface font-public-sans text-eab-neutral-900">
      <LandingHeader />
      <LandingHero imageUrl={heroImageUrl} />

      {contentBlocks?.map((block) => {
        switch (block.block_type) {
          case "featured_programs":
            return (
              <Fragment key={block.id}>
                <FeaturedPrograms
                  curricula={featuredCurricula}
                  isLoading={isFeaturedCurriculaLoading}
                />
                <PhotoGallery />
              </Fragment>
            );
          case "how_it_works":
            return <HowItWorks key={block.id} />;
          case "for_entrepreneurs_mentors":
            return <AudienceSplit key={block.id} />;
          case "cta":
            return <ClosingCTA key={block.id} />;
          default:
            return <CustomBlock key={block.id} block={block} />;
        }
      })}

      {/* 16px gutter below the last panel, matching the design's bottom margin. */}
      <div className="h-4" />

      <Footer />
    </div>
  );
}
